import { useAtomValue, useSetAtom } from 'jotai';
import { externalModalSignalsAtom } from '@js/store/modalAtoms';
import { useEffect, useState } from 'react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import useIsOpenedInExternalView from '@components/pages/modalExternalViewPage/hooks/useIsOpenedInExternalView';

export enum ExternalModalSignalId {
    InvalidateQuery = 'InvalidateQuery',
}

type ModalDataMap = {
    [ExternalModalSignalId.InvalidateQuery]: {
        queryKey: QueryKey;
    };
};

type ExternalModalSignal = {
    [K in ExternalModalSignalId]: {
        id: K;
        data: ModalDataMap[K];
    };
}[ExternalModalSignalId];

type SignalLibrary = {
    [K in ExternalModalSignalId]: (data: ModalDataMap[K]) => void;
};

const useExternalModalSignals = () => {
    const client = useQueryClient();
    const [listen, setListen] = useState(false);
    const isOpenedInExternalView = useIsOpenedInExternalView();
    const signals = useAtomValue(externalModalSignalsAtom) as Array<ExternalModalSignal>;
    const setSignals = useSetAtom(externalModalSignalsAtom);

    useEffect(() => {
        if (!signals?.length || !listen) {
            return;
        }

        signals.forEach((signal) => {
            signalLibrary[signal.id](signal.data);
        });
        setSignals([]);
    }, [signals, listen]);

    const sendSignal = (signals: Array<ExternalModalSignal>) => {
        if (!isOpenedInExternalView) {
            return;
        }
        setSignals(signals);
    };

    const signalLibrary: SignalLibrary = {
        [ExternalModalSignalId.InvalidateQuery]: (data) => {
            client.invalidateQueries({
                queryKey: data.queryKey,
            });
        },
    };

    return {
        listen: () => setListen(true),
        sendSignal,
    };
};

export default useExternalModalSignals;
